import { Pill, Stat } from '@salonomy/ui';
import { useStaffKpiReport, type ReportRange } from '../../data/reports.js';
import { exportCsvFilename } from '../../lib/csv.js';
import { money, percentFromBps, pluralize } from '../../lib/format.js';
import { ReportCard, type CsvExport } from './shared.js';

/**
 * WS9 — the per-staff KPI dashboard. REPLACES the old client-side per-chair cards (which summed appointment
 * priceQuoted in JS): completions, no-shows, revenue, average ticket, rebook + utilisation are ALL computed
 * server-side over recognised revenue (ADR-0009). Rates arrive as basis points → percentFromBps; money is
 * integer pence → money(). This surface only DISPLAYS the server's rows.
 */

export function StaffKpiCard({ range }: { range: ReportRange }) {
  const { data: report, isLoading, isError } = useStaffKpiReport(range);

  const isEmpty = !!report && report.rows.length === 0;

  const csv: CsvExport | null =
    report && report.rows.length > 0
      ? {
          filename: exportCsvFilename('staff-kpi'),
          headers: [
            'Staff',
            'Completed',
            'No-shows',
            'Cancellations',
            'No-show rate',
            'Revenue',
            'Average ticket',
            'Rebook rate',
            'Utilisation',
            'New clients',
            'Commission',
            'Earnings',
          ],
          rows: report.rows.map((r) => [
            r.staffName,
            r.completedAppointments,
            r.noShows,
            r.cancellations,
            percentFromBps(r.noShowRateBps),
            money(r.revenuePence),
            money(r.averageTicketPence),
            percentFromBps(r.rebookRateBps),
            percentFromBps(r.utilisationBps),
            r.newClients,
            money(r.commissionPence),
            money(r.earningsPence),
          ]),
        }
      : null;

  return (
    <ReportCard
      title="Staff KPIs"
      subtitle="Per-stylist completions, no-shows, revenue and rebook/utilisation rates over this period — server-computed."
      isLoading={isLoading}
      isError={isError}
      isEmpty={isEmpty}
      emptyLabel="No staff activity in this period."
      csv={csv}
    >
      {report ? (
        <div className="reports-grid">
          {report.rows.map((r) => (
            <div
              key={r.staffId}
              style={{
                border: '1px solid var(--slm-border)',
                borderRadius: 12,
                padding: 14,
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'baseline',
                  gap: 8,
                }}
              >
                <span style={{ fontWeight: 600 }}>{r.staffName}</span>
                <Pill tone="soft">{percentFromBps(r.utilisationBps)} util</Pill>
              </div>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                <Stat label="Revenue" value={money(r.revenuePence)} />
                <Stat label="Avg ticket" value={money(r.averageTicketPence)} />
                <Stat label="Earnings" value={money(r.earningsPence)} />
              </div>
              <div className="muted" style={{ fontSize: 12.5, lineHeight: 1.5 }}>
                {pluralize(r.completedAppointments, 'completed appointment')} · {r.noShows} no-show
                {r.noShows === 1 ? '' : 's'} ({percentFromBps(r.noShowRateBps)}) · {r.cancellations}{' '}
                cancelled · {percentFromBps(r.rebookRateBps)} rebook ·{' '}
                {pluralize(r.newClients, 'new client')} · {money(r.commissionPence)} commission
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </ReportCard>
  );
}
